import React, { useContext, useEffect, useState } from "react";
import { MenuItem } from "@mui/material";
import { useSnackbar } from "notistack";

import { api } from "../../../services/Api";
import Tabela from "../../../componentsv2/Tabela";
import { Input } from "../../../componentsv2/Input";
import Box from "../../../componentsv2/Box";
import { Context } from "../../../Context/PermissoesContext";
import ChipTipoProcedimento from "./ChipTipoProcedimento";
import {
  mesclarProcedimentosAtivosComVinculados,
  ordenarProcedimentos,
} from "../utils/ordenarProcedimentos";

const TIPOS_FILTRO = ["PROCEDIMENTO", "MEDICAMENTO", "ADICIONAL", "DESLOCAMENTO"];

const formatarMoeda = (valor) =>
  Number(valor ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

export default function TabelaProcedimentosAgendamento({
  setSelectedRows,
  atualizaTabela,
  initialItems = [],
  procedimentosVinculados = [],
  alturaTabela = 480,
}) {
  const [listaProcedimentos, setListaProcedimentos] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [quantidades, setQuantidades] = useState({});
  const [filtroTipo, setFiltroTipo] = useState("TODOS");
  const [busca, setBusca] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const { checarPermissao } = useContext(Context);

  const montarItensSelecionados = (ids, qtds, lista) =>
    ids.map((id) => {
      const procedimento = lista.find((p) => p.id === id);
      return {
        procedimentoId: id,
        quantidade: qtds[id] || 1,
        precoExame:
          procedimento?.agendamento_procedimento?.precoAplicado ??
          procedimento?.precoExame ??
          0,
        tipo: procedimento?.tipo,
      };
    });

  useEffect(() => {
    api
      .get("/v2/agendaCare/procedimentos")
      .then((response) => {
        const ativos = response.data.data || [];
        const lista = procedimentosVinculados.length
          ? mesclarProcedimentosAtivosComVinculados(ativos, procedimentosVinculados)
          : ordenarProcedimentos(ativos);
        setListaProcedimentos(lista);
      })
      .catch(() =>
        enqueueSnackbar("Não foi possível encontrar os procedimentos", { variant: "error" })
      );
  }, [atualizaTabela, enqueueSnackbar, procedimentosVinculados]);

  useEffect(() => {
    if (!initialItems.length) return;
    const ids = initialItems.map((item) => item.procedimentoId);
    const qtds = Object.fromEntries(
      initialItems.map((item) => [item.procedimentoId, item.quantidade])
    );
    setSelectedIds(ids);
    setQuantidades(qtds);
  }, [initialItems]);

  useEffect(() => {
    if (!listaProcedimentos.length || !selectedIds.length) return;
    setSelectedRows(montarItensSelecionados(selectedIds, quantidades, listaProcedimentos));
  }, [listaProcedimentos, selectedIds, quantidades, setSelectedRows]);

  const termoBusca = busca.trim().toLowerCase();
  const listaExibida = listaProcedimentos.filter((item) => {
    if (filtroTipo !== "TODOS" && item.tipo !== filtroTipo) return false;
    if (!termoBusca) return true;
    return (item.nome || "").toLowerCase().includes(termoBusca);
  });

  const notificarParent = (ids, qtds) => {
    setSelectedRows(montarItensSelecionados(ids, qtds, listaProcedimentos));
  };

  const handleSelecaoChange = (ids) => {
    setSelectedIds(ids);
    notificarParent(ids, quantidades);
  };

  const handleQuantidadeChange = (procedimentoId, valor) => {
    const quantidade = Math.max(1, Number(valor) || 1);
    const novasQuantidades = { ...quantidades, [procedimentoId]: quantidade };
    setQuantidades(novasQuantidades);
    notificarParent(selectedIds, novasQuantidades);
  };

  const columns = [
    {
      field: "nome",
      headerName: "Procedimento",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "precoExame",
      headerName: "Valor",
      headerAlign: "right",
      align: "right",
      hide: !checarPermissao("AGC002"),
      width: 100,
      renderCell: ({ value }) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatarMoeda(value)}</span>
      ),
    },
    {
      field: "tipo",
      headerName: "Tipo",
      headerAlign: "center",
      align: "center",
      width: 72,
      sortable: false,
      renderCell: ({ row }) => <ChipTipoProcedimento tipo={row.tipo} />,
    },
    {
      field: "quantidade",
      headerName: "Qtd",
      headerAlign: "center",
      align: "center",
      width: 68,
      sortable: false,
      renderCell: ({ row }) => {
        if (!selectedIds.includes(row.id)) return <span style={{ color: "#ccc" }}>—</span>;
        return (
          <input
            type="number"
            min={1}
            value={quantidades[row.id] || 1}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleQuantidadeChange(row.id, e.target.value)}
            style={{
              width: "50px",
              padding: "2px 4px",
              textAlign: "center",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <h4 style={{ margin: "0 0 12px 0" }}>Procedimentos</h4>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "minmax(120px, 22%) minmax(0, 1fr)" },
          gap: 2,
          mb: 1.5,
          alignItems: "end",
        }}
      >
        <Input
          select
          label="Tipo"
          fullWidth
          variant="standard"
          value={filtroTipo}
          onChange={(event) => setFiltroTipo(event.target.value)}
        >
          <MenuItem value="TODOS">Todos</MenuItem>
          {TIPOS_FILTRO.map((tipo) => (
            <MenuItem key={tipo} value={tipo}>
              {tipo}
            </MenuItem>
          ))}
        </Input>
        <Input
          fullWidth
          label="Buscar procedimento"
          variant="standard"
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
          placeholder="Filtrar por nome..."
        />
      </Box>
      <div style={{ height: alturaTabela, width: "100%" }}>
        <Tabela
          ocultarToolbar
          dataSet="Commodity"
          rowLength={listaExibida.length}
          rows={listaExibida}
          columns={columns}
          checkboxSelection
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={handleSelecaoChange}
          keepNonExistentRowsSelected
          sx={{
            "& .MuiDataGrid-columnHeader": {
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </div>
    </Box>
  );
}
